/**
 * Die vier Start-Heroes von DUELINE (Masterprompt Abschnitte 38, 56-62).
 *
 * Sie sind dauerhaft kostenlos verfuegbar.
 *
 * Stand v0.2: Hier stehen nur die Grunddaten, die fuer Bewegung und Darstellung
 * gebraucht werden. Skills, Talente und Capstones kommen mit dem jeweiligen
 * Hero-Meilenstein dazu (v0.4 Vanguard, v0.12 Arcanist, v0.13 Deadeye, v0.14 Shade).
 *
 * VERBINDLICH: id, name, role, resource
 * PLATZHALTER: alle Zahlenwerte (siehe tuning.ts)
 */

/** Rolle eines Heroes - bestimmt Silhouette und Spielgefuehl. */
export type HeroRole = 'tank' | 'mage' | 'ranger' | 'assassin';

/** Ressource, die ein Hero fuer seine Skills verbraucht. */
export type HeroResource = 'mana' | 'energy';

export interface HeroDefinition {
  id: string;
  /** Archetyp - so heisst er im Spiel. */
  name: string;
  /*
   * Name des Linebound aus der Lore (Lore v0.1).
   *
   * Wird im Spiel NICHT angezeigt - dort steht immer der Archetyp
   * (Produktentscheidung vom 02.10.2026). Er steht hier, damit es eine
   * einzige Quelle gibt: Die Homepage fuehrte ihn in einer eigenen
   * Tabelle, und zwei Stellen laufen frueher oder spaeter auseinander.
   */
  linebound: string;
  /**
   * Ein Satz, der die Figur beschreibt - fuer Menschen, nicht fuer die
   * Rechnung (15.10.2026).
   *
   * Er stand bis dahin nur auf der Homepage (HERO_PITCH). Seit es den
   * Steckbrief im Spiel gibt, braucht ihn auch der Client - und zwei
   * Stellen fuer denselben Text laufen frueher oder spaeter auseinander,
   * genau wie es beim Namen des Linebound schon einmal drohte.
   *
   * Englisch wie alle sichtbaren Texte.
   */
  pitch: string;
  role: HeroRole;
  resource: HeroResource;

  // --- Grundwerte (TUNING_PLACEHOLDER) ---
  /** Bewegung in Welteinheiten pro Sekunde. */
  moveSpeed: number;
  maxHp: number;
  maxResource: number;
  /** Radius der Kollisionsform auf der 2D-Simulationsebene. */
  radius: number;
  /**
   * Ressource, die pro Sekunde von selbst zurueckkommt.
   *
   * Seit v0.48 eine Eigenschaft des Heroes und keine Konstante fuer alle
   * (Produktentscheidung vom 29.09.2026).
   *
   * Grund aus der Messung ueber 720 Matches: Der Vanguard trank 20,8
   * Manatraenke je Match, der Arcanist 1,5. Er hat den kleinsten Pool im Spiel
   * (300 gegen 420 bis 520) und die teuersten Skills - und weil ein Trank
   * einen ANTEIL wiederherstellt, bekommt er fuer dieselben 100 Gold auch am
   * wenigsten zurueck. Mit einem Wert fuer alle war das nicht zu reparieren.
   *
   * Die Grundwerte stehen als MANA_REGEN_PER_SECOND und
   * ENERGY_REGEN_PER_SECOND in skills.ts - dort steht, was "normal" ist.
   */
  resourceRegen: number;

  // --- Normaler Angriff (TUNING_PLACEHOLDER) ---
  /** Schaden eines normalen Angriffs. */
  attackDamage: number;
  /** Angriffe pro Sekunde. */
  attackSpeed: number;
  /** Reichweite des normalen Angriffs. */
  attackRange: number;
}

export const HEROES: readonly HeroDefinition[] = [
  {
    id: 'vanguard',
    name: 'Vanguard',
    linebound: 'Rovan',
    pitch:
      'Holds the wave right where it stands. The more enemies crowd around him, the tougher he gets - and the more gold he earns.',
    role: 'tank',
    resource: 'mana',
    moveSpeed: 15,
    maxHp: 900,
    maxResource: 300,
    radius: 1.4,
    /*
     * 8 -> 5 (Produktentscheidung vom 05.10.2026).
     *
     * Die Regeneration deckte 57 Prozent seines Dauerfeuers - und ein
     * leerer Vorrat war in 38 Sekunden wieder voll, also waehrend des
     * Einkaufens. Mana war dadurch nie eine Entscheidung.
     *
     * Warum 5 und nicht 4 wie bei den anderen: Am 29.09.2026 wurde
     * festgelegt, dass er seinen billigsten Skill aus der Regeneration
     * bezahlen koennen muss. Der Grund war gemessen - ohne das trank er
     * 20,8 Manatraenke je Match, rund 2.000 Gold, die ihm fuer Wellen
     * fehlten. Er hat den kleinsten Vorrat im Spiel und die teuersten
     * Skills.
     *
     * Die Regel gilt jetzt fuer ZWEI Abklingzeiten statt einer: Er muss
     * sich einteilen, faellt aber nicht in das alte Loch zurueck.
     */
    resourceRegen: 5,
    attackDamage: 63,
    attackSpeed: 0.9,
    attackRange: 3,
  },
  {
    id: 'arcanist',
    name: 'Arcanist',
    linebound: 'Aelys',
    pitch:
      'Wipes out whole groups at once and buys time with thorned ground. Fragile up close: keep the wave at a distance.',
    role: 'mage',
    resource: 'mana',
    moveSpeed: 15.5,
    maxHp: 620,
    maxResource: 520,
    radius: 1.1,
    /*
     * 3 -> 4 (Produktentscheidung vom 06.10.2026).
     *
     * Gerechnet ueber alle vier Heroes: Er hat mit 85 Prozent den
     * groessten Teil seiner Wirkung in den Faehigkeiten und mit 3 die
     * niedrigste Regeneration. Haelt er alles auf Abklingzeit, kostet ihn
     * das 19,9 Mana je Sekunde - gedeckt sind davon 15 Prozent. Dauerhaft
     * bleiben ihm nur 28 Prozent seines moeglichen Schadens, bei den
     * anderen sind es 47 bis 61.
     *
     * Die 4 aendern daran wenig, und das ist Absicht: Mana soll eine
     * Entscheidung bleiben (siehe die Begruendung beim Vanguard). Es ist
     * eine Linderung, keine Loesung - der Hebel liegt bei den Kosten
     * seiner Skills oder bei deren Abklingzeiten.
     */
    resourceRegen: 4,
    attackDamage: 36,
    attackSpeed: 0.8,
    /*
     * Von 12 auf 18 (Produktentscheidung vom 25.10.2026).
     *
     * Robin wollte beide Fernkaempfer deutlich weiter schiessen lassen -
     * nur den normalen Angriff, nicht die Faehigkeiten. Damit reicht ihr
     * Schuss weiter als Lashing Vines (9) und genauso weit wie Thornburst
     * und Bramble Field (16) plus zwei; nur Deadfall liegt mit 20 darueber.
     */
    attackRange: 18,
  },
  {
    id: 'deadeye',
    name: 'Deadeye',
    linebound: 'Calder',
    pitch:
      'Brings down one hard target from further away than anyone else. Every shot on the same target hits harder than the last.',
    role: 'ranger',
    resource: 'mana',
    moveSpeed: 16,
    maxHp: 650,
    maxResource: 420,
    radius: 1.1,
    resourceRegen: 3,
    attackDamage: 61,
    attackSpeed: 1.1,
    /*
     * Von 14 auf 20 (Produktentscheidung vom 25.10.2026) - zusammen mit
     * dem Arcanist. Sein Schuss reicht damit weiter als Snare Trap (14)
     * und Barrage (18); Piercing Shot (22) und Kill Shot (30) bleiben
     * darueber.
     */
    attackRange: 20,
  },
  {
    id: 'shade',
    name: 'Shade',
    linebound: 'Vessa',
    pitch:
      'The fastest Linebound on the line. Every kill builds momentum - keep moving, keep cutting, never stand still.',
    role: 'assassin',
    resource: 'energy',
    moveSpeed: 17,
    /*
     * Von 640 auf 480 (v0.50), dann auf 550 (v0.51).
     *
     * Er soll Schaden machen und dabei staendig Gefahr laufen zu sterben - das
     * ist die Rolle, und mit 640 war er nicht einmal der Zerbrechlichste
     * (Arcanist 620).
     *
     * Die 480 waren fuer die neue Wirtschaft zu hart: Sie wurden gemessen, als
     * ein Match 8 Minuten dauerte und bei Level 8,8 endete. Seit v0.51 endet es
     * bei Level 10,9, und Level gibt PROZENTUALE Zuschlaege - davon hat der
     * Hero mit dem wenigsten Grundleben absolut am wenigsten. Er fiel dadurch
     * auf 25,3 Prozent Siegquote.
     *
     * 550 laesst ihn den Zerbrechlichsten bleiben, ohne ihn zu erledigen.
     */
    maxHp: 550,
    /*
     * Energy bleibt bei 200 (Produktentscheidung vom 05.10.2026).
     *
     * Nachdem die Regeneration am selben Tag von 12 auf 7 ging, fiel seine
     * Siegquote von 55,6 / 49,3 auf 35,4 / 33,3 Prozent. Die naheliegende
     * Erklaerung war der Puffer: Mit 200 hat er vier Zauber im Vorrat, die
     * anderen Heroes sechs bis acht.
     *
     * Gemessen stimmte das NICHT. Mit 280 fiel er auf 27,8 / 27,8 Prozent -
     * zweimal derselbe Wert, also kein Streuen. Er wirkt damit zwar mehr
     * (101,5 -> 106,1 Kills), stirbt aber oefter (5,4 -> 5,8) und richtet
     * insgesamt weniger Schaden an. Der genaue Mechanismus ist offen.
     *
     * Festgehalten, damit niemand denselben naheliegenden Schluss noch
     * einmal zieht: Mehr Vorrat hilft dem Shade nicht.
     */
    maxResource: 200,
    radius: 1.0,
    /*
     * 12 -> 7 (Produktentscheidung vom 05.10.2026).
     *
     * Energy kommt weiter schneller zurueck als Mana (Abschnitt 51) - aber
     * mit 12 deckte sie 92 Prozent seines Dauerfeuers ab. Er konnte ueber
     * drei Minuten am Stueck wirken und war in 17 Sekunden wieder voll.
     * Eine Ressource, die man nie einteilen muss, ist keine.
     */
    resourceRegen: 7,
    attackDamage: 42,
    attackSpeed: 1.5,
    attackRange: 3,
  },
];

export const HEROES_BY_ID: ReadonlyMap<string, HeroDefinition> = new Map(
  HEROES.map((hero) => [hero.id, hero]),
);

export function getHero(id: string): HeroDefinition | undefined {
  return HEROES_BY_ID.get(id);
}

/**
 * Hero, der verwendet wird, solange es noch keine Hero-Auswahl gibt.
 * Die Auswahl kommt in v0.15 (Full Match Flow).
 */
export const DEFAULT_HERO_ID = 'vanguard';
